import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  Archive,
  ClipboardList,
  Copy,
  Ellipsis,
  ExternalLink,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { FLOW_KIND_LABEL, FLOW_STATUS_LABEL, type FlowSummary } from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { CopyButton } from "@/components/flows/fields";
import { NewFlowDialog } from "@/components/flows/NewFlowDialog";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hint } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api";
import { relativeTime } from "@/lib/crm";
import {
  errorMessage,
  useArchiveFlow,
  useDeleteFlow,
  useDuplicateFlow,
  useFlows,
} from "@/lib/flows";

export const Route = createFileRoute("/_authenticated/formularios/")({
  component: FormulariosPage,
});

const TOM_STATUS: Record<FlowSummary["status"], NonNullable<BadgeProps["variant"]>> = {
  published: "success",
  draft: "muted",
  archived: "outline",
};

type Filtro = "todos" | "form" | "quiz";

/**
 * Formulários e quizzes.
 *
 * A lista responde "o que está no ar e está trazendo gente?" — por isso o
 * número da semana fica na própria linha (iniciados → concluídos → leads),
 * sem abrir o relatório de cada um.
 *
 * O Typechat usa o mesmo motor, mas tem área própria; aqui ele não aparece.
 */
function FormulariosPage() {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [novo, setNovo] = useState(false);
  const [excluindo, setExcluindo] = useState<FlowSummary | null>(null);

  const fluxos = useFlows();
  const duplicar = useDuplicateFlow();
  const arquivar = useArchiveFlow();
  const excluir = useDeleteFlow();

  const todos = useMemo(
    () => (fluxos.data ?? []).filter((f) => f.kind === "form" || f.kind === "quiz"),
    [fluxos.data],
  );
  const linhas = useMemo(
    () =>
      todos
        .filter((f) => filtro === "todos" || f.kind === filtro)
        // Arquivados por último: estão aqui só para consulta.
        .sort((a, b) => Number(a.status === "archived") - Number(b.status === "archived")),
    [todos, filtro],
  );
  const noAr = todos.filter((f) => f.status === "published").length;

  const acoes = {
    abrir: (f: FlowSummary) =>
      void navigate({ to: "/formularios/$flowId", params: { flowId: f.id } }),
    duplicar: (f: FlowSummary) =>
      duplicar.mutate(f.id, {
        onSuccess: (copia) => {
          toast.success("Cópia criada como rascunho");
          void navigate({ to: "/formularios/$flowId", params: { flowId: copia.id } });
        },
        onError: (e) => toast.error(errorMessage(e, "Não foi possível duplicar")),
      }),
    arquivar: (f: FlowSummary) =>
      arquivar.mutate(f.id, {
        onSuccess: () => toast.success("Arquivado: o link para de abrir o formulário"),
        onError: (e) => toast.error(errorMessage(e, "Não foi possível arquivar")),
      }),
    excluir: (f: FlowSummary) => setExcluindo(f),
  };

  return (
    <>
      <PageHeader
        title="Formulários"
        description={
          fluxos.data
            ? `${noAr} no ar de ${todos.length} · números dos últimos 7 dias`
            : "Formulários e quizzes que viram lead no CRM"
        }
        action={
          <Button onClick={() => setNovo(true)}>
            <Plus />
            Novo
          </Button>
        }
      >
        <Tabs defaultValue="todos" variant="pills" onValueChange={(v) => setFiltro(v as Filtro)}>
          <TabsList>
            <TabsTrigger value="todos">Todos</TabsTrigger>
            <TabsTrigger value="form">Formulários</TabsTrigger>
            <TabsTrigger value="quiz">Quizzes</TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>

      <PageBody>
        <Card className="overflow-hidden">
          {fluxos.isPending ? (
            <div className="divide-y" aria-busy>
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                  <Skeleton className="size-9 rounded-lg" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-48 max-w-[60%]" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="hidden h-5 w-20 rounded-full md:block" />
                  <Skeleton className="hidden h-5 w-32 rounded-full lg:block" />
                </div>
              ))}
            </div>
          ) : fluxos.isError ? (
            <EmptyState
              icon={ClipboardList}
              title="Não foi possível carregar os formulários"
              description="Confira a conexão e tente de novo."
              action={
                <Button variant="outline" size="sm" onClick={() => void fluxos.refetch()}>
                  Tentar de novo
                </Button>
              }
            />
          ) : linhas.length === 0 ? (
            todos.length === 0 ? (
              <EmptyState
                icon={ClipboardList}
                title="Nenhum formulário ainda"
                description="Monte uma página rápida — formulário de uma pergunta por tela ou quiz com resultado — e use o link no anúncio. Cada resposta vira lead no CRM com a campanha de onde veio, inclusive quem parou no meio."
                action={
                  <Button size="sm" onClick={() => setNovo(true)}>
                    <Plus />
                    Criar o primeiro
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={ClipboardList}
                title={filtro === "quiz" ? "Nenhum quiz" : "Nenhum formulário"}
                description="Troque a aba para ver os outros ou crie um novo."
              />
            )
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5">Nome</th>
                      <th className="px-4 py-2.5">Tipo</th>
                      <th className="px-4 py-2.5">Situação</th>
                      <th className="px-4 py-2.5">Versão</th>
                      <th className="px-4 py-2.5">Últimos 7 dias</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {linhas.map((f) => (
                      <tr
                        key={f.id}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("a, button, [role=menuitem]"))
                            return;
                          acoes.abrir(f);
                        }}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="px-4 py-2.5">
                          <Link
                            to="/formularios/$flowId"
                            params={{ flowId: f.id }}
                            className="block max-w-72 truncate font-semibold hover:text-primary"
                          >
                            {f.name}
                          </Link>
                          <p className="truncate text-xs text-muted-foreground">
                            /f/{f.slug} · editado {relativeTime(f.updatedAt)}
                          </p>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant="soft">{FLOW_KIND_LABEL[f.kind]}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge variant={TOM_STATUS[f.status]}>
                            {FLOW_STATUS_LABEL[f.status]}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                          {f.publishedVersion ? `v${f.publishedVersion}` : "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          <Semana stats={f.stats} />
                        </td>
                        <td className="px-4 py-2.5">
                          <Acoes fluxo={f} acoes={acoes} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="divide-y md:hidden">
                {linhas.map((f) => (
                  <li key={f.id} className="flex items-start gap-2 px-4 py-3">
                    <Link
                      to="/formularios/$flowId"
                      params={{ flowId: f.id }}
                      className="min-w-0 flex-1"
                    >
                      <p className="truncate text-sm font-semibold">{f.name}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge variant="soft">{FLOW_KIND_LABEL[f.kind]}</Badge>
                        <Badge variant={TOM_STATUS[f.status]}>{FLOW_STATUS_LABEL[f.status]}</Badge>
                        {f.publishedVersion && (
                          <Badge variant="outline">v{f.publishedVersion}</Badge>
                        )}
                      </div>
                      <div className="mt-1.5">
                        <Semana stats={f.stats} />
                      </div>
                    </Link>
                    <Acoes fluxo={f} acoes={acoes} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </PageBody>

      <NewFlowDialog open={novo} onOpenChange={setNovo} />

      <Dialog open={Boolean(excluindo)} onOpenChange={(o) => !o && setExcluindo(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Excluir “{excluindo?.name}”?</DialogTitle>
            <DialogDescription>
              Some da lista e o link deixa de abrir. Não dá para desfazer. Se ele já recebeu
              respostas, arquive em vez de excluir — as respostas e os leads continuam.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcluindo(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={excluir.isPending}
              onClick={() =>
                excluindo &&
                excluir.mutate(excluindo.id, {
                  onSuccess: () => {
                    toast.success("Formulário excluído");
                    setExcluindo(null);
                  },
                  onError: (e) => {
                    toast.error(
                      e instanceof ApiError && e.status === 409
                        ? "Este formulário já tem respostas. Arquive em vez de excluir."
                        : errorMessage(e, "Não foi possível excluir"),
                    );
                    setExcluindo(null);
                  },
                })
              }
            >
              {excluir.isPending ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** "12 → 8 → 7 · 67%": iniciados, concluídos, leads e a taxa de conclusão. */
function Semana({ stats }: { stats: FlowSummary["stats"] }) {
  const taxa = stats.starts > 0 ? Math.round((stats.completes / stats.starts) * 100) : null;
  if (stats.starts === 0) {
    return <span className="text-xs text-muted-foreground">sem respostas na semana</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5 text-xs tabular-nums">
      <Hint label="Iniciados → concluídos → leads">
        <span>
          <span className="font-medium">{stats.starts.toLocaleString("pt-BR")}</span>
          <span className="text-muted-foreground"> → </span>
          <span className="font-medium">{stats.completes.toLocaleString("pt-BR")}</span>
          <span className="text-muted-foreground"> → </span>
          <span className="font-semibold text-primary">{stats.leads.toLocaleString("pt-BR")}</span>
        </span>
      </Hint>
      {taxa !== null && <span className="text-muted-foreground">· {taxa}% concluem</span>}
    </span>
  );
}

function Acoes({
  fluxo: f,
  acoes,
}: {
  fluxo: FlowSummary;
  acoes: Record<"abrir" | "duplicar" | "arquivar" | "excluir", (f: FlowSummary) => void>;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <CopyButton text={f.publicUrl} label="Copiar link" size="icon-xs" variant="ghost" />
      <DropdownMenu>
        <Hint label="Mais ações">
          <DropdownMenuTrigger asChild>
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={`Ações de ${f.name}`}
              className="text-muted-foreground"
            >
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
        </Hint>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => acoes.abrir(f)}>
            <Pencil />
            Abrir editor
          </DropdownMenuItem>
          {f.status === "published" && (
            <DropdownMenuItem asChild>
              <a href={f.publicUrl} target="_blank" rel="noreferrer">
                <ExternalLink />
                Ver página
              </a>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => acoes.duplicar(f)}>
            <Copy />
            Duplicar
          </DropdownMenuItem>
          {f.status !== "archived" && (
            <DropdownMenuItem onSelect={() => acoes.arquivar(f)}>
              <Archive />
              Arquivar
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onSelect={() => acoes.excluir(f)}>
            <Trash2 />
            Excluir
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
